/**
 * Brands list page — operator browses every brand the system has surfaced,
 * filters by decision/size, and clicks through to the detail page to mark
 * selected / rejected. Mirrors the SuppliersPage list pattern (bordered
 * table + filter pills) for visual consistency.
 *
 * No Keepa cost — pure cache read from `keepa_brand_cache` joined per-tenant
 * to `brand_decisions`.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Badge, Card, Input, cn } from '../design';
import { CategorySelect } from '../components/CategorySelect';
import { useBrandsList, type BrandsListParams } from '../lib/brands';

type DecisionFilter = NonNullable<BrandsListParams['decision']>;
type SizeFilter = 'micro' | 'small' | 'medium' | 'large';

const DECISION_PILLS: Array<{ key: DecisionFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'undecided', label: 'Undecided' },
  { key: 'selected', label: 'Selected' },
  { key: 'rejected', label: 'Rejected' },
];

const SIZE_LABELS: Record<SizeFilter, string> = {
  micro: 'Micro',
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
};

function sizeBadgeVariant(size: SizeFilter | null): 'neutral' | 'accent' | 'positive' {
  if (size === 'micro' || size === 'small') return 'positive';
  if (size === 'medium') return 'accent';
  return 'neutral';
}

function decisionBadgeVariant(
  decision: 'selected' | 'rejected' | null
): 'neutral' | 'positive' | 'negative' {
  if (decision === 'selected') return 'positive';
  if (decision === 'rejected') return 'negative';
  return 'neutral';
}

/**
 * Compose a Reviews-column value from the two enrichment paths. Deep-fetch
 * gives an exact `totalReviewCount`; summary gives `avgReviewCount` per SKU
 * which we fall back to with a "/SKU" suffix so the column is never empty
 * for an enriched brand.
 */
function formatReviews(totalReviewCount: number | null, avgReviewCount: number | null): string {
  if (totalReviewCount != null) return totalReviewCount.toLocaleString();
  if (avgReviewCount != null) return `${avgReviewCount.toLocaleString()}/SKU`;
  return '—';
}

/**
 * Pick whichever enrichment timestamp is freshest. Returns a relative
 * "2h ago" string for compactness in the table cell; null when the brand
 * has never been enriched.
 */
function formatLastEnriched(deepFetchedAt: string | null, lastSummaryAt: string | null): string {
  const t = [deepFetchedAt, lastSummaryAt]
    .filter((x): x is string => Boolean(x))
    .map((x) => new Date(x).getTime());
  if (t.length === 0) return '—';
  const newest = Math.max(...t);
  const ageMs = Date.now() - newest;
  const minutes = Math.round(ageMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function BrandsPage() {
  const [decision, setDecision] = useState<DecisionFilter>('all');
  const [sizes, setSizes] = useState<SizeFilter[]>([]);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useBrandsList({
    decision,
    size: sizes.length > 0 ? sizes : undefined,
    q: q.trim() || undefined,
    category: category ?? undefined,
    sort: 'product_count',
    dir: 'desc',
  });

  function toggleSize(s: SizeFilter): void {
    setSizes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight">Brands</h1>
              <p className="text-xs text-fg-tertiary">
                Every brand surfaced by your searches. Mark selected / rejected to gate enrichment.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-8 space-y-5">
        {data?.rejectionCapExceeded && (
          <Card className="border-accent-primary/30 bg-accent-primary/[0.06]">
            <p className="text-sm">
              You&apos;ve rejected {data.rejectedTotal} brands. Only the 100 most recent are
              filtered out of Keepa searches at the source — older ones still get filtered
              downstream but cost a small amount of token budget per surfacing. Consider reviewing
              the oldest rejections.
            </p>
          </Card>
        )}

        {/* Filter pills */}
        <div className="space-y-3">
          <div
            className="flex items-center gap-2 flex-wrap"
            role="tablist"
            aria-label="Decision filter"
          >
            {DECISION_PILLS.map((f) => (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={decision === f.key}
                onClick={() => setDecision(f.key)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
                  decision === f.key
                    ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                    : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-fg-tertiary mr-1">Size:</span>
            {(Object.keys(SIZE_LABELS) as SizeFilter[]).map((s) => {
              const active = sizes.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSize(s)}
                  aria-pressed={active}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
                    active
                      ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                      : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
                  )}
                >
                  {SIZE_LABELS[s]}
                </button>
              );
            })}
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 max-w-md min-w-[200px]">
              <label
                htmlFor="brand-search"
                className="text-xs uppercase tracking-wider text-fg-tertiary block mb-1.5"
              >
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-fg-tertiary" />
                <Input
                  id="brand-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search brand name…"
                  className="pl-9"
                />
              </div>
            </div>
            <div className="min-w-[200px]">
              <label
                htmlFor="brand-category"
                className="text-xs uppercase tracking-wider text-fg-tertiary block mb-1.5"
              >
                Category
              </label>
              <CategorySelect id="brand-category" value={category} onChange={setCategory} />
            </div>
          </div>
        </div>

        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Failed to load brands: {(error as Error).message ?? 'Unknown error'}
            </p>
          </Card>
        )}

        {data && data.rows.length === 0 && (
          <Card className="text-center py-14">
            <h2 className="text-base font-medium mb-2">No brands match</h2>
            <p className="text-sm text-fg-secondary max-w-md mx-auto">
              Run a search on the home page to discover brands. They&apos;ll show up here as the
              brand-enrichment sweep deep-fetches them.
            </p>
          </Card>
        )}

        {data && data.rows.length > 0 && (
          <>
            <div className="text-xs text-fg-tertiary">
              {data.rows.length} of {data.total} brand{data.total === 1 ? '' : 's'}
              {data.rejectedTotal > 0 && (
                <span className="ml-2">· {data.rejectedTotal} rejected</span>
              )}
            </div>
            <div className="border border-border-subtle rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-1 text-xs text-fg-tertiary">
                  <tr>
                    <th className="text-left font-medium px-4 py-2.5">Brand</th>
                    <th className="text-left font-medium px-4 py-2.5">Size</th>
                    <th className="text-right font-medium px-4 py-2.5 tabular-nums">Products</th>
                    <th className="text-right font-medium px-4 py-2.5 tabular-nums">Reviews</th>
                    <th className="text-left font-medium px-4 py-2.5">Enriched</th>
                    <th className="text-left font-medium px-4 py-2.5">Decision</th>
                    <th className="text-left font-medium px-4 py-2.5">Decided</th>
                    <th className="px-2" />
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((b) => (
                    <tr
                      key={`${b.domain}:${b.name}`}
                      className="border-t border-border-subtle hover:bg-surface-1/60 transition-colors duration-fast"
                    >
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{b.name}</div>
                        {b.decisionNotesPreview && (
                          <div className="text-xs text-fg-tertiary mt-0.5 line-clamp-1">
                            {b.decisionNotesPreview}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {b.sizeBucket ? (
                          <Badge variant={sizeBadgeVariant(b.sizeBucket)}>
                            {SIZE_LABELS[b.sizeBucket]}
                          </Badge>
                        ) : (
                          <span className="text-xs text-fg-tertiary">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {b.productCount ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {formatReviews(b.totalReviewCount, b.avgReviewCount)}
                      </td>
                      <td className="px-4 py-2.5 text-fg-secondary text-xs">
                        {formatLastEnriched(b.deepFetchedAt, b.lastSummaryAt)}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant={decisionBadgeVariant(b.decision)}>
                          {b.decision === 'selected'
                            ? 'Selected'
                            : b.decision === 'rejected'
                              ? 'Rejected'
                              : 'Undecided'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-fg-secondary text-xs">
                        {b.decidedAt ? new Date(b.decidedAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-2 py-2.5 text-right">
                        <Link
                          to="/brands/$name"
                          params={{ name: b.name }}
                          className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast inline-flex items-center"
                          aria-label={`Open ${b.name}`}
                        >
                          <ChevronRight className="size-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
